import {
  UI_SECRET_ACCESS,
  type UiConfirmCapability,
  type UiElementAccess,
  type UiReadOnlyCapability,
} from '@streamlain/copilot-realtime-contract';

/**
 * The AI operates everything the user sees. This is the one list of
 * exceptions, by area or function of the app: an element inherits the access
 * of the area it sits in, so new elements need no registration and no element
 * declares a category itself. The server enforces the categories; this list
 * only names which area an element belongs to.
 *
 * Category 1 is everything not listed here; categories 2 and 3 are the rules
 * below, category 4 the secret content after them.
 */
interface UiAccessRule {
  /** The area or function, matched on the element or its ancestors. */
  within: string;
  /** Narrows an area to the one containing this marker (e.g. a dialog's input). */
  containing?: string;
  access: UiElementAccess;
}

const read = (capability: UiReadOnlyCapability): UiElementAccess => ({
  level: 'read',
  capability,
});
const confirm = (capability: UiConfirmCapability): UiElementAccess => ({
  level: 'confirm',
  capability,
});
/** What a delete control removes, named on it by `data-ui-delete`. */
const DELETE_TARGETS = {
  document: 'document.delete',
  database: 'database.delete',
  app: 'app_base.delete',
} as const satisfies Record<string, UiConfirmCapability>;

const settingsTabs = (tabs: readonly string[]) =>
  tabs.map(tab => `[data-setting-tab="workspace:${tab}"]`).join(',');

export const UI_ACCESS_RULES: readonly UiAccessRule[] = [
  // Category 3: the AI may open and read these places, never act in them.
  {
    within: '[role="dialog"]',
    containing: '[data-testid="delete-workspace-input"]',
    access: read('workspace.delete'),
  },
  {
    within: '[data-ui-area="account-deletion"]',
    access: read('account.delete'),
  },
  {
    within: '[data-testid="delete-page-button"],[data-ui-area="trash-purge"]',
    access: read('data.purge'),
  },
  {
    within:
      '[data-testid="page-history-modal"],[data-testid="confirm-restore-history-modal"]',
    access: read('history.restore'),
  },
  // Category 2: the AI acts only after the user confirms.
  {
    within:
      '[data-testid="slider-bar-invite-members-button"],[data-testid="invite-modal"],[data-ui-area="invite"]',
    access: confirm('people.invite'),
  },
  { within: '[data-ui-area="share"]', access: confirm('document.share') },
  // Who reaches an App and what they may do there: page rights, portal people,
  // groups and permissions. Choosing what to look at stays free.
  {
    within: '[data-ui-area="app-access"]',
    access: confirm('app_base.access'),
  },
  // Sending a mail reaches people outside the workspace.
  { within: '[data-ui-area="mail-send"]', access: confirm('mail.send') },
  {
    within: settingsTabs([
      'members',
      'guests',
      'groups',
      'roles',
      'permissions',
      'permission-graph',
      'team-areas',
      'security',
      'saml',
      'mcp-access',
    ]),
    access: confirm('people.manage'),
  },
  // Deleting something with a capability of its own asks under that one, so
  // a workspace rule for deleting it holds for the click too.
  ...Object.entries(DELETE_TARGETS).map(([target, capability]) => ({
    within: `[data-ui-delete="${target}"]`,
    access: confirm(capability),
  })),
  {
    within:
      '[data-variant="error"],[data-destructive="true"],[role="menuitem"].danger,[role="menuitemcheckbox"].danger,[role="menuitemradio"].danger',
    access: confirm('ui.destructive'),
  },
];

/**
 * Category 4: content that never leaves the browser. The element is listed
 * with its label so the AI knows it is there; its value and everything inside
 * it are not read, and the AI does not write it.
 */
const UI_SECRET_CONTENT = [
  'input[type="password" i]',
  'input[autocomplete$="password" i]',
  'input[autocomplete^="cc-" i]',
  'input[autocomplete="one-time-code" i]',
  '[data-secret]',
  '[data-sensitive]',
].join(',');

/** Whether the element's content is secret (category 4). */
export function isSecretUiContent(element: Element): boolean {
  return element.matches(UI_SECRET_CONTENT);
}

const MAX_ORIGIN_HOPS = 4;
const POPUP_ROOT = '[role="menu"],[role="listbox"],[role="dialog"]';

/**
 * The element plus the triggers of the popups it sits in: a menu portaled
 * out of an area still belongs to the area that opened it.
 */
function origins(element: Element): Element[] {
  const chain = [element];
  let current: Element | null = element;
  for (let hop = 0; current && hop < MAX_ORIGIN_HOPS; hop++) {
    const popup: Element | null = current.closest(POPUP_ROOT);
    const id: string | undefined = popup?.id;
    if (!id) break;
    const trigger: Element | null = element.ownerDocument.querySelector(
      `[aria-controls="${CSS.escape(id)}"]`
    );
    if (!trigger || chain.includes(trigger)) break;
    chain.push(trigger);
    current = trigger;
  }
  return chain;
}

/**
 * The access of an element; absent means free. A read-only place wins over
 * secret content, secret content over confirmation.
 */
export function classifyUiAccess(
  element: Element
): UiElementAccess | undefined {
  const anchors = origins(element);
  let access: UiElementAccess | undefined = isSecretUiContent(element)
    ? UI_SECRET_ACCESS
    : undefined;
  for (const rule of UI_ACCESS_RULES) {
    for (const anchor of anchors) {
      const area = anchor.closest(rule.within);
      if (!area || (rule.containing && !area.querySelector(rule.containing)))
        continue;
      if (rule.access.level === 'read') return rule.access;
      access ??= rule.access;
    }
  }
  return access;
}
